import { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Save } from 'lucide-react';
import axios from 'axios';
import AdminLayout from './AdminLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FAQAdmin = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingFaq, setEditingFaq] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const emptyFaq = { question: '', answer: '', order: 0, active: true };

  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/admin/faq`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFaqs(response.data);
    } catch (err) {
      console.error('Failed to fetch FAQs', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingFaq.id) {
        await axios.put(`${API}/admin/faq/${editingFaq.id}`, editingFaq, { headers });
      } else {
        await axios.post(`${API}/admin/faq`, editingFaq, { headers });
      }
      
      setShowModal(false);
      setEditingFaq(null);
      fetchFaqs();
    } catch (err) {
      alert('Failed to save FAQ: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API}/admin/faq/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFaqs();
    } catch (err) {
      alert('Failed to delete FAQ');
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="faq-admin-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 uppercase">FAQ Management</h1>
            <p className="text-slate-500 mt-1">Manage frequently asked questions</p>
          </div>
          <button
            onClick={() => { setEditingFaq({ ...emptyFaq, order: faqs.length }); setShowModal(true); }}
            className="btn-primary flex items-center gap-2"
            data-testid="add-faq-btn"
          >
            <Plus className="w-5 h-5" />
            Add FAQ
          </button>
        </div>

        {/* FAQs List */}
        <div className="space-y-3">
          {faqs.map((faq, index) => (
            <div key={faq.id} className="bg-white border-2 border-slate-100 p-4" data-testid={`faq-item-${faq.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5">#{index + 1}</span>
                    <span className={`text-xs px-2 py-0.5 ${faq.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                      {faq.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <h3 className="font-semibold text-slate-900 mt-2">{faq.question}</h3>
                  <p className="text-slate-500 text-sm mt-1 line-clamp-2">{faq.answer}</p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setEditingFaq(faq); setShowModal(true); }}
                    className="p-2 text-slate-400 hover:text-orange-500"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(faq.id)}
                    className="p-2 text-slate-400 hover:text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {faqs.length === 0 && (
          <div className="bg-white border-2 border-slate-100 p-12 text-center">
            <p className="text-slate-500">No FAQs yet. Add your first question!</p>
          </div>
        )}

        {/* Edit Modal */}
        {showModal && editingFaq && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white w-full max-w-xl">
              <div className="border-b border-slate-100 p-6 flex items-center justify-between">
                <h2 className="font-heading text-2xl font-bold text-slate-900 uppercase">
                  {editingFaq.id ? 'Edit FAQ' : 'Add FAQ'}
                </h2>
                <button onClick={() => { setShowModal(false); setEditingFaq(null); }} className="p-2 text-slate-400 hover:text-slate-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Question *</label>
                  <input
                    type="text"
                    value={editingFaq.question}
                    onChange={(e) => setEditingFaq({ ...editingFaq, question: e.target.value })}
                    className="w-full h-12 px-4 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="Enter the question"
                  />
                </div>

                <div>
                  <label className="block font-heading text-xs uppercase tracking-wider text-slate-500 mb-2">Answer *</label>
                  <textarea
                    value={editingFaq.answer}
                    onChange={(e) => setEditingFaq({ ...editingFaq, answer: e.target.value })}
                    rows={5}
                    className="w-full px-4 py-3 border-2 border-slate-200 bg-slate-50 focus:border-orange-500"
                    placeholder="Enter the answer"
                  />
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={editingFaq.active}
                    onChange={(e) => setEditingFaq({ ...editingFaq, active: e.target.checked })}
                    className="w-5 h-5"
                  />
                  <label className="text-slate-700">Active (visible on website)</label>
                </div>
              </div>

              <div className="border-t border-slate-100 p-6 flex justify-end gap-4">
                <button onClick={() => { setShowModal(false); setEditingFaq(null); }} className="px-6 py-3 text-slate-600 hover:bg-slate-100">
                  Cancel
                </button>
                <button onClick={handleSave} className="btn-primary flex items-center gap-2">
                  <Save className="w-5 h-5" />
                  Save FAQ
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FAQAdmin;
